import { Flex, IconButton, Image, Spinner } from '@chakra-ui/react';
import { useInfiniteScroll } from 'hooks/useInfiniteScrollHook';
import { useResizeObserver } from 'hooks/useResizeObserver';
import { CSSProperties, memo, useMemo, useRef, useState } from 'react';
import { VariableSizeGrid as Grid } from 'react-window';
import { ImageHit } from 'types/imageLibsTypes';

import { COLUMN_NUM, GUTTER_SIZE, INDEX_OFFSET } from '../InfiniteGrid/InfiniteGrid.config';
import { usePhotoMenu } from './PhotoMenuContext';
import { calculateColumnWidth } from './PhotoWidgetMenu.helper';

// Props required by react-window
// @see https://github.com/bvaughn/react-window/blob/master/src/createGridComponent.js
interface CellProps<T> {
  columnIndex: number;
  data: T;
  isScrolling?: boolean;
  rowIndex: number;
  style: CSSProperties;
}

interface GridInterface {
  hits: ImageHit[];
  onLoadMoreAnchor: (instance: Element | null) => void;
}

interface InfiniteListProps<T = any> {
  data: T[];
  numberOfItems: number;
  loadMore: () => void;
}

export const InfiniteList = memo(({ data, numberOfItems, loadMore }: InfiniteListProps<ImageHit>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [, onLoadAnchor] = useInfiniteScroll({ onLoadMore: loadMore, onLoadThreshold: 0.8 });

  // Update the infinite scroll box size when the window is resizing
  useResizeObserver({
    ref: containerRef,
    onResize: ({ width = 0, height = 0 }) => {
      setContainerSize({ width, height });
    },
  });

  const calculateRowHeight = (rowIndex: number) => {
    const imageIndex = rowIndex * COLUMN_NUM;

    // Get the max height of images on a row
    const maxHeightInARow = Math.max(data[imageIndex].previewHeight, data[imageIndex + 1]?.previewHeight || 0);
    return maxHeightInARow + GUTTER_SIZE;
  };

  const gridData = useMemo(() => {
    return {
      onLoadMoreAnchor: onLoadAnchor,
      hits: data,
    };
  }, [data, onLoadAnchor]);

  return (
    <Flex
      ref={containerRef}
      direction='column'
      h='full'
      overflow={'auto'}
      marginTop={3}
      data-testid={'infinite-image-list'}
    >
      <Grid<GridInterface>
        itemData={gridData}
        columnCount={COLUMN_NUM}
        columnWidth={calculateColumnWidth}
        height={containerSize.height}
        width={containerSize.width}
        rowHeight={calculateRowHeight}
        rowCount={Math.ceil(numberOfItems / 2)}
      >
        {Cell}
      </Grid>
    </Flex>
  );
});
InfiniteList.displayName = 'InfiniteList';

export const Cell = ({ columnIndex, rowIndex, style, data }: CellProps<GridInterface>) => {
  const index = rowIndex * COLUMN_NUM + columnIndex;
  const { hits, onLoadMoreAnchor } = data;
  const { onItemClick } = usePhotoMenu();
  const hit = hits[index];

  const [isAddingImage, setIsAddingImage] = useState(false);

  if (!hit) return null;

  const onClick = () => {
    setIsAddingImage(true);
    onItemClick(hit, index).finally(() => {
      setIsAddingImage(false);
    });
  };

  const isAnchorCell = index === hits.length - INDEX_OFFSET;

  return (
    <Flex
      // Set unique key for each item to avoid re-rendering when the data array is changed
      key={`${index}-${hit.id}-${isAnchorCell}`}
      // Set loadmore anchor ref near the end of the list
      ref={isAnchorCell ? onLoadMoreAnchor : undefined}
      style={style}
      justifyContent={'center'}
      alignItems={'center'}
    >
      <IconButton aria-label={hit.alt} w='full' h='full' onClick={onClick} variant={'icon-btn-side-menu'} padding={2}>
        <Image src={hit.previewURL} alt={hit.alt} maxHeight={'100%'} />
      </IconButton>
      {isAddingImage && <LoadingOverlay />}
    </Flex>
  );
};

export const LoadingOverlay = () => {
  return (
    <Flex
      w='full'
      h='full'
      position={'absolute'}
      top={0}
      left={0}
      justifyContent={'center'}
      alignItems={'center'}
      cursor={'not-allowed'}
      bg='blackAlpha.300'
      data-testid={'image-adding-overlay'}
    >
      <Spinner color={'brand.500'} />
    </Flex>
  );
};
